import { useState, useMemo } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { sheetsAPI } from '../lib/api';
import { useAuth } from '../lib/auth';
import { queryKeys } from '../lib/queryClient';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { BackgroundRefreshIndicator, CallCardSkeleton } from '../components/ui/loading-states';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { toast } from 'sonner';
import { 
    Phone, 
    RefreshCw, 
    ChevronDown, 
    Clock, 
    DollarSign,
    FileText,
    X,
    Filter,
    ChevronLeft,
    ChevronRight,
    Copy,
    Check,
    Info
} from 'lucide-react';
import { useDashboardStatus } from '../hooks/useDashboardStatus';
import SetupInProgressBanner from '../components/SetupInProgressBanner';

// Helper function to format transcript with bold labels and spacing
const formatTranscript = (transcript) => {
    if (!transcript) return null;
    
    // Split by "User:" or "Agent:" while keeping the delimiter
    const parts = transcript.split(/(?=(User:|Agent:))/g).filter(Boolean);
    
    if (parts.length <= 1) {
        // No User/Agent pattern found, return as-is
        return <span>{transcript}</span>;
    }
    
    return parts.map((part, index) => {
        const isUser = part.startsWith('User:');
        const isAgent = part.startsWith('Agent:');
        
        if (isUser) {
            // Remove "User:" and trim whitespace/newlines
            const content = part.substring(5).trim();
            if (!content) return null; // Skip empty entries
            return (
                <div key={index} className={index > 0 ? 'mt-3' : ''}>
                    <span className="font-bold text-gray-900">User:</span>
                    <span className="ml-1">{content}</span>
                </div>
            );
        } else if (isAgent) {
            // Remove "Agent:" and trim whitespace/newlines
            const content = part.substring(6).trim();
            if (!content) return null; // Skip empty entries
            return (
                <div key={index} className={index > 0 ? 'mt-3' : ''}>
                    <span className="font-bold text-brand-orange-600">Agent:</span>
                    <span className="ml-1">{content}</span>
                </div>
            );
        }
        return <span key={index}>{part}</span>;
    }).filter(Boolean); // Remove null entries
};

const CallLogCard = ({ call }) => {
    const [transcriptOpen, setTranscriptOpen] = useState(false);
    const [copied, setCopied] = useState(false);
    
    const formatDuration = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = Math.round(seconds % 60);
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };
    
    const copyCallId = async () => {
        try {
            await navigator.clipboard.writeText(call.id);
            setCopied(true);
            toast.success('Call ID copied to clipboard');
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            toast.error('Failed to copy');
        }
    };

    return (
        <Card className="overflow-hidden border-l-4 border-l-gray-200" data-testid={`call-log-${call.id}`}>
            <CardContent className="p-5">
                {/* Header with Phone and Function */}
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-2 gap-2">
                    <div className="flex items-center gap-2">
                        <Phone className="w-4 h-4 text-gray-500" />
                        <span className="font-mono font-semibold text-gray-900 text-lg">{call.phone_number}</span>
                    </div>
                    {call.function_performed && (
                        <span className="px-3 py-1.5 bg-brand-orange-500 text-white text-xs sm:text-sm font-medium rounded-lg break-words whitespace-normal leading-tight inline-block w-fit">
                            {call.function_performed}
                        </span>
                    )}
                </div>

                {/* Date/Time */}
                <p className="text-sm text-gray-500 mb-3">{call.timestamp}</p>

                {/* Duration and Cost */}
                <div className="flex items-center gap-4 mb-4 text-sm text-gray-600">
                    <div className="flex items-center gap-1">
                        <Clock className="w-4 h-4 text-gray-400" />
                        <span>{formatDuration(call.duration_seconds)}</span>
                    </div>
                    <div className="flex items-center gap-1">
                        <DollarSign className="w-4 h-4 text-gray-400" />
                        <span>{(call.cost || 0).toFixed(8)}</span>
                    </div>
                </div>

                {/* Summary */}
                {call.summary && (
                    <div className="mb-4">
                        <h4 className="text-sm font-semibold text-gray-900 mb-1">Summary</h4>
                        <p className="text-sm text-gray-600 leading-relaxed">{call.summary}</p>
                    </div>
                )}

                {/* Transcript */}
                {call.transcript && (
                    <Collapsible open={transcriptOpen} onOpenChange={setTranscriptOpen}>
                        <CollapsibleTrigger asChild>
                            <Button variant="ghost" className="w-full justify-between px-0 hover:bg-transparent h-auto py-2">
                                <span className="text-sm font-semibold text-gray-900 flex items-center gap-1">
                                    Transcript
                                    <TooltipProvider>
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <Info className="w-3.5 h-3.5 text-gray-400 hover:text-gray-600 cursor-help" />
                                            </TooltipTrigger>
                                            <TooltipContent className="max-w-xs">
                                                <p className="text-xs">Transcripts are AI-generated and may contain errors. Refer to the call audio for the most accurate information.</p>
                                            </TooltipContent>
                                        </Tooltip>
                                    </TooltipProvider>
                                </span>
                                <ChevronDown className={`w-4 h-4 transition-transform ${transcriptOpen ? 'rotate-180' : ''}`} />
                            </Button>
                        </CollapsibleTrigger>
                        <CollapsibleContent>
                            <div className="p-3 bg-gray-50 rounded-lg text-sm text-gray-600 max-h-60 overflow-y-auto">
                                {formatTranscript(call.transcript)}
                            </div>
                        </CollapsibleContent>
                    </Collapsible>
                )}

                {/* Audio Recording */}
                {call.audio_url && (
                    <div className="mt-4">
                        <h4 className="text-sm font-semibold text-gray-900 mb-2">Call Recording</h4>
                        <audio controls className="w-full h-10" data-testid={`audio-${call.id}`}>
                            <source src={call.audio_url} type="audio/mpeg" />
                            Your browser does not support the audio element.
                        </audio>
                    </div>
                )}

                {/* Call ID */}
                <div className="mt-4 pt-3 border-t border-gray-100">
                    <div className="flex items-center gap-2">
                        <p className="text-xs text-gray-400">Call ID: {call.id}</p>
                        <button
                            onClick={copyCallId}
                            className="p-1 rounded hover:bg-gray-100 transition-colors group"
                            title="Copy Call ID"
                        >
                            {copied ? (
                                <Check className="w-3.5 h-3.5 text-green-500" />
                            ) : (
                                <Copy className="w-3.5 h-3.5 text-gray-400 group-hover:text-orange-500" />
                            )}
                        </button>
                    </div>
                </div>
            </CardContent>
        </Card>
    );
};

export default function CallLogsPage() {
    const { user } = useAuth();
    const { ready: dashboardReady } = useDashboardStatus();
    const [dataSource, setDataSource] = useState('database');
    
    // Helper to format date for input
    const formatDateForInput = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    // Default date range: yesterday to today
    const getDefaultDates = () => {
        const today = new Date();
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        return {
            start: formatDateForInput(yesterday),
            end: formatDateForInput(today)
        };
    };

    // Filters - default to yesterday and today
    const defaultDates = getDefaultDates();
    const [startDate, setStartDate] = useState(defaultDates.start);
    const [endDate, setEndDate] = useState(defaultDates.end);
    const [phoneFilter, setPhoneFilter] = useState('');
    const [functionFilter, setFunctionFilter] = useState('all');
    
    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const ITEMS_PER_PAGE = 10;

    // Fetch call logs using React Query
    const { 
        data: callsData, 
        isLoading: loading, 
        refetch: refetchCalls,
        isFetching: refreshing
    } = useQuery({
        queryKey: queryKeys.callLogs(user?.company_id),
        queryFn: async () => {
            const response = await sheetsAPI.getCallLogsFromSheet(user.company_id);
            setDataSource(response.data.source || 'database');
            return response.data.calls || [];
        },
        enabled: !!user?.company_id,
        staleTime: 2 * 60 * 1000, // 2 minutes
        refetchOnWindowFocus: true, // Refetch when user returns to tab
        // Timed background refresh. Reads come from Mongo (see server.py's
        // `read_source` default), so this doesn't burn Google Sheets quota.
        // Paused when tab is in the background to avoid needless work.
        refetchInterval: 30000,
        refetchIntervalInBackground: false,
        placeholderData: keepPreviousData, // SWR: show cached calls instantly while refetching
    });

    const calls = useMemo(() => callsData || [], [callsData]);

    // Get unique functions for filter dropdown
    const uniqueFunctions = useMemo(() => {
        const functions = new Set();
        calls.forEach(call => {
            if (call.function_performed) {
                functions.add(call.function_performed);
            }
        });
        return Array.from(functions).sort();
    }, [calls]);

    // Helper function to parse timestamp from Google Sheets format
    const parseTimestamp = (timestamp) => {
        if (!timestamp) return null;
        try {
            // Handle format: "12/26/2025 8:13 AM" or "12/26/2025 7:14 PM"
            const parsed = new Date(timestamp);
            if (!isNaN(parsed.getTime())) return parsed;
            
            // Manual parsing for "MM/DD/YYYY H:MM AM/PM" format
            const match = timestamp.match(/(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{1,2}):(\d{2})\s*(AM|PM)?/i);
            if (match) {
                let [, month, day, year, hours, minutes, ampm] = match;
                hours = parseInt(hours);
                if (ampm) {
                    if (ampm.toUpperCase() === 'PM' && hours !== 12) hours += 12;
                    if (ampm.toUpperCase() === 'AM' && hours === 12) hours = 0;
                }
                return new Date(year, month - 1, day, hours, parseInt(minutes));
            }
            return null;
        } catch {
            return null;
        }
    };

    // Filter calls
    const filteredCalls = useMemo(() => {
        return calls.filter(call => {
            // Date filter
            if (startDate && call.timestamp) {
                const callDate = parseTimestamp(call.timestamp);
                if (!callDate) return true; // If can't parse, include it
                
                // Parse start date components to avoid timezone issues
                const [startYear, startMonth, startDay] = startDate.split('-').map(Number);
                const filterStartDate = new Date(startYear, startMonth - 1, startDay, 0, 0, 0, 0);
                
                if (callDate < filterStartDate) return false;
            }
            if (endDate && call.timestamp) {
                const callDate = parseTimestamp(call.timestamp);
                if (!callDate) return true; // If can't parse, include it
                
                // Parse end date components to avoid timezone issues
                const [endYear, endMonth, endDay] = endDate.split('-').map(Number);
                const filterEndDate = new Date(endYear, endMonth - 1, endDay, 23, 59, 59, 999);
                
                if (callDate > filterEndDate) return false;
            }
            
            // Phone filter
            if (phoneFilter && !call.phone_number?.includes(phoneFilter)) {
                return false;
            }
            
            // Function filter
            if (functionFilter !== 'all') {
                if (functionFilter === 'none' && call.function_performed) return false;
                if (functionFilter !== 'none' && call.function_performed !== functionFilter) return false;
            }
            
            return true;
        });
    }, [calls, startDate, endDate, phoneFilter, functionFilter]);

    // Paginated calls
    const paginatedCalls = useMemo(() => {
        const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
        return filteredCalls.slice(startIndex, startIndex + ITEMS_PER_PAGE);
    }, [filteredCalls, currentPage]);

    const totalPages = Math.ceil(filteredCalls.length / ITEMS_PER_PAGE);

    // Calls by function stats
    const callsByFunction = useMemo(() => {
        const stats = {};
        calls.forEach(call => {
            const func = call.function_performed || 'No Function';
            stats[func] = (stats[func] || 0) + 1;
        });
        return Object.entries(stats).sort((a, b) => b[1] - a[1]);
    }, [calls]);

    const handleRefresh = () => {
        refetchCalls();
    };

    // Reset to page 1 when filters change
    const handleFilterChange = (setter, value) => {
        setter(value);
        setCurrentPage(1);
    };

    const clearFilters = () => {
        const defaults = getDefaultDates();
        setStartDate(defaults.start);
        setEndDate(defaults.end);
        setPhoneFilter('');
        setFunctionFilter('all');
        setCurrentPage(1);
    };

    // Check if background refresh is happening
    const isBackgroundRefreshing = refreshing && calls.length > 0;

    // Check if filters are modified from defaults
    const hasFilters = startDate !== defaultDates.start || endDate !== defaultDates.end || phoneFilter || functionFilter !== 'all';

    return (
        <div className="p-4 md:p-8 bg-gray-50 min-h-screen">
            {!dashboardReady && <SetupInProgressBanner />}
            {/* Background Refresh Indicator */}
            <BackgroundRefreshIndicator isRefetching={isBackgroundRefreshing} />
            
            {/* Header - Mobile-friendly layout */}
            <div className="mb-6">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div>
                        <h2 className="text-2xl font-heading font-bold text-gray-900 flex items-center gap-2">
                            <Phone className="w-7 h-7 text-brand-orange-500" />
                            Call Log
                        </h2>
                        <p className="text-gray-500 mt-1">View all incoming call records</p>
                    </div>
                    <Button 
                        variant="outline" 
                        onClick={handleRefresh} 
                        disabled={refreshing}
                        className="w-full md:w-auto"
                        data-testid="refresh-calls-btn"
                    >
                        <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                        Refresh
                    </Button>
                </div>
            </div>

            {/* Filters Section */}
            <Card className="mb-6 overflow-hidden">
                <CardContent className="p-4 overflow-x-hidden">
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-2">
                            <Filter className="w-4 h-4 text-gray-500" />
                            <span className="font-medium text-gray-900">Filters</span>
                        </div>
                        {hasFilters && (
                            <Button variant="ghost" size="sm" onClick={clearFilters} className="text-gray-500 hover:text-gray-700">
                                <X className="w-4 h-4 mr-1" />
                                Clear All
                            </Button>
                        )}
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        {/* Date Range */}
                        <div className="md:col-span-2 grid grid-cols-2 gap-2">
                            <div className="min-w-0">
                                <label className="text-xs text-gray-500 mb-1 block">Date Range</label>
                                <Input
                                    type="date"
                                    value={startDate}
                                    onChange={(e) => handleFilterChange(setStartDate, e.target.value)}
                                    className="text-sm w-full max-w-full"
                                    placeholder="mm/dd/yyyy"
                                />
                            </div>
                            <div className="min-w-0">
                                <label className="text-xs text-gray-500 mb-1 block">&nbsp;</label>
                                <Input
                                    type="date"
                                    value={endDate}
                                    onChange={(e) => handleFilterChange(setEndDate, e.target.value)}
                                    className="text-sm w-full max-w-full"
                                    placeholder="mm/dd/yyyy"
                                />
                            </div>
                        </div>
                        
                        {/* Phone Number */}
                        <div>
                            <label className="text-xs text-gray-500 mb-1 block">Phone Number</label>
                            <Input
                                type="text"
                                value={phoneFilter}
                                onChange={(e) => handleFilterChange(setPhoneFilter, e.target.value)}
                                placeholder="Filter by phone"
                                className="text-sm"
                            />
                        </div>
                        
                        {/* Function */}
                        <div className="min-w-0">
                            <label className="text-xs text-gray-500 mb-1 block">Function</label>
                            <Select value={functionFilter} onValueChange={(v) => handleFilterChange(setFunctionFilter, v)}>
                                <SelectTrigger className="text-sm">
                                    <SelectValue placeholder="All Functions" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">All Functions</SelectItem>
                                    <SelectItem value="none">No Function</SelectItem>
                                    {uniqueFunctions.map(func => (
                                        <SelectItem key={func} value={func}>{func}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Calls by Function - Expandable */}
            <Collapsible className="mb-6">
                <Card>
                    <CollapsibleTrigger asChild>
                        <CardContent className="p-4 cursor-pointer hover:bg-gray-50 flex items-center justify-between">
                            <span className="font-medium text-gray-900">Calls by Function</span>
                            <div className="flex items-center gap-2 text-gray-500 text-sm">
                                <span>Expand</span>
                                <ChevronDown className="w-4 h-4" />
                            </div>
                        </CardContent>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                        <CardContent className="pt-0 pb-4 px-4">
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                {callsByFunction.map(([func, count]) => (
                                    <div key={func} className="p-3 bg-gray-50 rounded-lg">
                                        <p className="text-sm font-medium text-gray-900">{func}</p>
                                        <p className="text-2xl font-bold text-brand-orange-500">{count}</p>
                                    </div>
                                ))}
                            </div>
                        </CardContent>
                    </CollapsibleContent>
                </Card>
            </Collapsible>

            {/* Call Logs List */}
            <div className="space-y-4" data-testid="call-logs-list">
                {loading ? (
                    Array.from({ length: 3 }).map((_, i) => (
                        <CallCardSkeleton key={i} />
                    ))
                ) : paginatedCalls.length === 0 ? (
                    <Card>
                        <CardContent className="p-8 text-center text-gray-500">
                            <Phone className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                            <p>No call logs found</p>
                            {hasFilters && (
                                <p className="text-sm mt-2">Try adjusting your filters</p>
                            )}
                        </CardContent>
                    </Card>
                ) : (
                    paginatedCalls.map((call, idx) => (
                        <CallLogCard key={call.id || idx} call={call} />
                    ))
                )}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-6 bg-white rounded-lg p-4 border">
                    <p className="text-sm text-gray-500 text-center sm:text-left">
                        Showing {((currentPage - 1) * ITEMS_PER_PAGE) + 1} - {Math.min(currentPage * ITEMS_PER_PAGE, filteredCalls.length)} of {filteredCalls.length} calls
                    </p>
                    <div className="flex items-center gap-1 sm:gap-2">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                            disabled={currentPage === 1}
                            className="px-2 sm:px-3"
                        >
                            <ChevronLeft className="w-4 h-4" />
                            <span className="hidden sm:inline ml-1">Previous</span>
                        </Button>
                        <div className="flex items-center gap-1">
                            {Array.from({ length: Math.min(3, totalPages) }, (_, i) => {
                                let pageNum;
                                if (totalPages <= 3) {
                                    pageNum = i + 1;
                                } else if (currentPage <= 2) {
                                    pageNum = i + 1;
                                } else if (currentPage >= totalPages - 1) {
                                    pageNum = totalPages - 2 + i;
                                } else {
                                    pageNum = currentPage - 1 + i;
                                }
                                return (
                                    <Button
                                        key={pageNum}
                                        variant={currentPage === pageNum ? 'default' : 'outline'}
                                        size="sm"
                                        className={`w-8 h-8 p-0 ${currentPage === pageNum ? 'bg-brand-orange-500 hover:bg-brand-orange-600' : ''}`}
                                        onClick={() => setCurrentPage(pageNum)}
                                    >
                                        {pageNum}
                                    </Button>
                                );
                            })}
                        </div>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                            disabled={currentPage === totalPages}
                            className="px-2 sm:px-3"
                        >
                            <span className="hidden sm:inline mr-1">Next</span>
                            <ChevronRight className="w-4 h-4" />
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
}
